import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    Draft,
    SerializedError,
} from '@reduxjs/toolkit';
import { IAction, SlicePropsWithData } from '../../store';
import { urlParamReplicaId, urlProjectFileCompare } from '../../../Constants';
import { apiFetch } from '../../../util/ProjectUtils';

/**
 * Initial slice state value.
 */
const initialState: IProjectDocumentsComparisonState = {
    loading: false,
    error: null,
    data: null,
};

/**
 * Collection of the reducers for this slice.
 */
const reducers = {
    resetDocumentsComparisonError: (state: Draft<IProjectDocumentsComparisonState>) => {
        state.error = null;
    },
};

/**
 * Object of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Async function that opens two documents in comparison mode.
     * @param config - Object that contains the project database's replicaId, document Ids and current view.
     * @returns Promise.
     */
    compareDocuments: async (config: {
        replicaId: string;
        // Document Universal Ids joined with ";" coming from DocumentLoaderView
        universalId: string;
    }): Promise<string> => {
        const { replicaId, universalId } = config;
        try {
            const url = urlProjectFileCompare.replace(urlParamReplicaId, replicaId);
            const request: RequestInit = {
                method: 'GET',
                headers: {
                    universalIds: universalId,
                },
            };

            const response = await apiFetch(url, request);

            return response.ok
                ? Promise.resolve(await response.text())
                : Promise.reject(new Error(await response.text()));
        } catch (error) {
            return Promise.reject(error as Error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IProjectDocumentsComparisonState>) => {
        /**
         * Reducers for the 'comparisonDocuments' thunk.
         */
        builder.addCase(
            compareDocuments.pending,
            (state: Draft<IProjectDocumentsComparisonState>) => {
                state.loading = true;
                state.error = null;
            },
        );
        builder.addCase(
            compareDocuments.fulfilled,
            (state: Draft<IProjectDocumentsComparisonState>, action: IAction<string>) => {
                state.loading = false;
                state.error = null;
                state.data = action.payload;
            },
        );
        builder.addCase(
            compareDocuments.rejected,
            (state: Draft<IProjectDocumentsComparisonState>, action: IAction<SerializedError>) => {
                state.loading = false;
                state.error = action.error;
            },
        );
    },
};

/**
 * Expose thunk functions as exports.
 */
export const compareDocuments = createAsyncThunk(
    'project/documents/comparison',
    thunks.compareDocuments,
);

/**
 * Interface that describes the state object for this redux slice.
 */
export type IProjectDocumentsComparisonState = SlicePropsWithData<string>;

/**
 * Create the redux slice.
 */
export const projectDocumentsComparisonSlice = createSlice({
    name: 'projectDocumentsComparison',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

/**
 * Export functions from slice actions.
 */
export const { resetDocumentsComparisonError } = projectDocumentsComparisonSlice.actions;

/**
 * Export slice reducer as default.
 */
export default projectDocumentsComparisonSlice.reducer;
